import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const STORAGE_KEY = "tappanon:scroll-positions";
const MAX_ENTRIES = 50;
/** How long a restore waits for the page to grow tall enough (cached lists render at once; refetches take longer). */
const RESTORE_TIMEOUT_MS = 3000;

type Positions = Record<string, number>;

function readPositions(): Positions {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Positions) : {};
  } catch {
    return {};
  }
}

function writePosition(key: string, y: number) {
  try {
    const positions = readPositions();
    delete positions[key];
    positions[key] = y;
    const keys = Object.keys(positions);
    for (const old of keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES))) delete positions[old];
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
  } catch {
    // Storage full or blocked: back navigation simply won't restore.
  }
}

const maxScroll = () => document.documentElement.scrollHeight - window.innerHeight;

/**
 * A history entry's storage key. The router gives every freshly loaded page the
 * key "default", so the path keeps two such pages from sharing a position.
 */
const entryKey = (key: string, pathname: string, search: string) => `${key}|${pathname}${search}`;

/**
 * Scroll behaviour for client-side navigation, which the browser can't handle on
 * its own: it restores a history entry's position before the page has rendered,
 * so a long list comes back capped near the top.
 *
 * - Back/forward (POP): return to where the member was on that entry, once the
 *   page is tall enough; a wheel, touch or key press cancels the restore.
 * - A new page (pathname change): start at the top.
 * - Same page, different query or hash (tabs, filters): leave the position alone.
 */
export default function ScrollManager() {
  const { key, pathname, search } = useLocation();
  const navigationType = useNavigationType();
  const keyRef = useRef(entryKey(key, pathname, search));
  const pathRef = useRef<string | null>(null);
  const restoringRef = useRef(false);

  useEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  // Remember the position of whichever entry is showing, as the member scrolls.
  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (restoringRef.current || frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        writePosition(keyRef.current, Math.round(window.scrollY));
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  useLayoutEffect(() => {
    keyRef.current = entryKey(key, pathname, search);
    const samePage = pathRef.current === pathname;
    pathRef.current = pathname;

    if (navigationType !== "POP") {
      if (!samePage) window.scrollTo(0, 0);
      return;
    }

    const target = readPositions()[keyRef.current];
    if (target === undefined) return;

    restoringRef.current = true;
    const started = performance.now();
    let frame = 0;
    const finish = () => {
      restoringRef.current = false;
      cancelAnimationFrame(frame);
      window.removeEventListener("wheel", cancel);
      window.removeEventListener("touchstart", cancel);
      window.removeEventListener("keydown", cancel);
    };
    // The member took over; don't yank them back.
    const cancel = () => finish();
    const attempt = () => {
      if (maxScroll() >= target || performance.now() - started > RESTORE_TIMEOUT_MS) {
        window.scrollTo(0, Math.min(target, Math.max(0, maxScroll())));
        finish();
        return;
      }
      frame = requestAnimationFrame(attempt);
    };
    window.addEventListener("wheel", cancel, { passive: true });
    window.addEventListener("touchstart", cancel, { passive: true });
    window.addEventListener("keydown", cancel);
    attempt();
    return finish;
  }, [key, pathname, search, navigationType]);

  return null;
}
